import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useSEO, SITE_URL } from '@/lib/seo';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter, CONTACT_EMAIL } from '@/components/SiteFooter';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  { value: 'general', label: 'General enquiry' },
  { value: 'recipe-submission', label: 'Submit a recipe' },
  { value: 'recipe-correction', label: 'Recipe correction' },
  { value: 'nutrition-correction', label: 'Nutrition / calorie correction' },
  { value: 'copyright', label: 'Copyright concern' },
  { value: 'advertising', label: 'Advertising enquiry' },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initialTopic = TOPICS.some((t) => t.value === params.get('topic'))
    ? params.get('topic')
    : 'general';

  const [form, setForm] = useState({
    name: '',
    email: '',
    category: initialTopic,
    subject: '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useSEO({
    title: 'Contact Masala Spin — Corrections, Submissions & Enquiries',
    description:
      'Contact Masala Spin about recipe corrections, nutrition corrections, copyright concerns, recipe submissions or advertising. Email masalaspin@erkupaetta.resend.app.',
    canonical: `${SITE_URL}/contact`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Masala Spin',
      url: `${SITE_URL}/contact`,
      mainEntity: {
        '@type': 'Organization',
        name: 'Masala Spin',
        url: SITE_URL,
        email: CONTACT_EMAIL,
      },
    },
  });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/feedback`, {
        name: form.name,
        email: form.email,
        category: form.category,
        recipe_name: form.subject,
        recipe_description: form.message,
      });
      toast.success(data.message);
      setSent(true);
      setForm({ name: '', email: '', category: 'general', subject: '', message: '' });
    } catch (err) {
      toast.error('Could not send your message. Please email us directly instead.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 py-14">
        <Breadcrumbs items={[{ name: 'Contact' }]} />

        <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1C1917] tracking-tight mb-5">
          Contact Masala Spin
        </h1>
        <p className="font-['Outfit'] text-[#1C1917]/75 leading-relaxed text-lg mb-8">
          Masala Spin is an independently run Indian recipe site. Write to us about anything on it —
          we read every message.
        </p>

        <div className="p-6 rounded-2xl bg-white border border-[#1C1917]/5 mb-10">
          <h2 className="text-xs uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold mb-3">
            Email us directly
          </h2>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="contact-email-link"
            className="inline-flex items-center gap-2 font-['Outfit'] text-base sm:text-lg text-[#1C1917] hover:text-[#F97316] transition-colors break-all"
          >
            <Mail className="w-5 h-5 text-[#F97316] flex-shrink-0" aria-hidden="true" />
            {CONTACT_EMAIL}
          </a>
          <p className="font-['Outfit'] text-sm text-[#1C1917]/60 mt-4 mb-2">
            Please get in touch about:
          </p>
          <ul className="list-disc pl-6 font-['Outfit'] text-sm text-[#1C1917]/70 space-y-1.5">
            <li>General enquiries about the site</li>
            <li>Recipe corrections — a wrong ingredient, step or cooking time</li>
            <li>Nutrition corrections — a calorie estimate or dietary tag that looks wrong</li>
            <li>Copyright concerns, including images or text you believe are yours</li>
            <li>Recipe submissions you would like us to publish</li>
            <li>Advertising enquiries</li>
          </ul>
        </div>

        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1C1917] mb-2">
          Or send a message here
        </h2>
        <p className="font-['Outfit'] text-sm text-[#1C1917]/60 mb-6">
          We use your name and email only to reply to you. See the{' '}
          <Link to="/privacy-policy" className="text-[#F97316] hover:underline">privacy policy</Link>{' '}
          for how submissions are handled.
        </p>

        {sent ? (
          <div
            className="p-6 rounded-2xl border border-[#2D4A22]/25 bg-[#2D4A22]/5 font-['Outfit'] text-[#1C1917]/80"
            data-testid="contact-success"
          >
            <p className="font-semibold mb-1">Thanks — your message has been received.</p>
            <p className="text-sm">
              If you do not hear back, email us directly at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#F97316] hover:underline break-all">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 font-['Outfit']" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-[#1C1917] mb-1.5">
                  Your name
                </label>
                <Input
                  id="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="contact-name-input"
                  className="border-[#1C1917]/20 focus:border-[#F97316]"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-[#1C1917] mb-1.5">
                  Your email
                </label>
                <Input
                  id="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="contact-email-input"
                  className="border-[#1C1917]/20 focus:border-[#F97316]"
                />
              </div>
            </div>

            <div>
              <label htmlFor="contact-topic" className="block text-sm font-medium text-[#1C1917] mb-1.5">
                What is this about?
              </label>
              <select
                id="contact-topic"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="contact-topic-select"
                className="w-full h-10 px-3 rounded-md border border-[#1C1917]/20 bg-white text-sm focus:border-[#F97316] focus:outline-none"
              >
                {TOPICS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="contact-subject" className="block text-sm font-medium text-[#1C1917] mb-1.5">
                Subject
              </label>
              <Input
                id="contact-subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                required
                placeholder="e.g. Wrong cooking time on Bajra Khichdi"
                data-testid="contact-subject-input"
                className="border-[#1C1917]/20 focus:border-[#F97316]"
              />
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-[#1C1917] mb-1.5">
                Message
              </label>
              <Textarea
                id="contact-message"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                data-testid="contact-message-input"
                className="border-[#1C1917]/20 focus:border-[#F97316]"
              />
            </div>

            <Button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold px-8 py-3 rounded-full transition-colors"
            >
              <Send className="w-4 h-4 mr-2" aria-hidden="true" />
              {sending ? 'Sending…' : 'Send message'}
            </Button>
          </form>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
